<!--
  ~ Copyright (C) 2025 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

<section class="feedback-stats-section" [id]="section?.uid">
  <div class="feedback-stats-section-top">
    <div class="feedback-stats-title large bold">{{ section?.title }}</div>
  </div>

  <div class="feedback-stats-section-inner">
    <ng-container *ngFor="let stats of section?.feedbackStats">
      <mat-card
        [matTooltip]="getTooltip(stats)"
        class="feedback-stats-item item-avg-score"
        *ngIf="stats.type === 'AVG_SCORE' && stats.enabled"
      >
        <div class="item-title medium bold">{{ 'translations.diagnostics.feedbackStats.evaluatorFeedbackAverage' | transloco }}</div>
        <div class="score-value" *ngIf="!isInvalidValue(stats.avgSelectedFeedbackScores); else avgScoreEmpty">
          {{ formatPercent(stats.avgSelectedFeedbackScores, '1.0-0') }}
        </div>
        <ng-template #avgScoreEmpty>
          <div class="empty-state">
            <mat-icon class="icon">info</mat-icon>
            <div>
              <div class="title small bold">{{ 'translations.diagnostics.feedbackStats.notEnoughData' | transloco }}</div>
              <div class="text extra-small">{{ 'translations.diagnostics.feedbackStats.collectMoreAnswers' | transloco }}</div>
            </div>
          </div>
        </ng-template>
      </mat-card>

      <mat-card
        [matTooltip]="getTooltip(stats)"
        class="feedback-stats-item item-avg-ratings"
        *ngIf="stats.type === 'AVG_RATING' && stats.enabled"
      >
        <div class="item-title medium bold">{{ 'translations.diagnostics.feedbackStats.overallCareerReadiness' | transloco }}</div>
        <div
          class="rating-value"
          *ngIf="
            !isInvalidValue(stats.avgUserScoreFromAllAttempts) && !isInvalidValue(stats.avgSelectedProvidersScores);
            else avgRatingEmpty
          "
        >
          <ptl-feedback-results-range-chart
            class="rating-chart"
            [userScore]="stats.avgUserScoreFromAllAttempts"
            [feedbackProvidersAvgScores]="stats.avgSelectedProvidersScores"
          >
          </ptl-feedback-results-range-chart>
        </div>
        <ng-template #avgRatingEmpty>
          <div class="empty-state">
            <mat-icon class="icon">info</mat-icon>
            <div>
              <div class="title small bold">{{ 'translations.diagnostics.feedbackStats.notEnoughData' | transloco }}</div>
              <div class="text extra-small">{{ 'translations.diagnostics.feedbackStats.collectMoreAnswers' | transloco }}</div>
            </div>
          </div>
        </ng-template>
      </mat-card>

      <mat-card
        [matTooltip]="getTooltip(stats)"
        class="feedback-stats-item distance-travelled"
        *ngIf="stats.type === 'DISTANCE_TRAVELLED' && stats.enabled"
      >
        <div class="item-title medium bold">
          <svg class="svg" aria-hidden="true"><use ogSvgUnify="assets/sprite/sprite.svg#path"></use></svg>
          {{ 'translations.diagnostics.feedbackStats.distanceTravelled' | transloco }}
        </div>
        <div class="distance-travelled-description small">
          {{
            'translations.diagnostics.feedbackStats.distanceTravelledScoreChanged'
              | transloco: { date: stats.since ? (stats.since | dateFormat: 'DD MMM yyyy') : '' }
          }}
        </div>
        <div class="distance-travelled-value" *ngIf="!isInvalidValue(stats.percentageChange); else distanceEmpty">
          <ng-container [ngSwitch]="isPositiveChange(stats.percentageChange)">
            <div *ngSwitchCase="true" class="change-indicator">
              <mat-icon class="positive">arrow_upward</mat-icon>
              <span class="chip positive">{{ formatPercent(stats.percentageChange) }}</span>
            </div>
            <div *ngSwitchCase="false" class="change-indicator">
              <mat-icon class="negative">arrow_downward</mat-icon>
              <span class="chip negative">{{ formatPercent(stats.percentageChange) }}</span>
            </div>
            <div *ngSwitchDefault class="change-indicator">
              <span class="chip">{{ formatPercent(stats.percentageChange) }}</span>
            </div>
          </ng-container>
        </div>
        <ng-template #distanceEmpty>
          <div class="empty-state">
            <mat-icon class="icon">info</mat-icon>
            <div>
              <div class="title small bold">{{ 'translations.diagnostics.feedbackStats.notEnoughData' | transloco }}</div>
              <div class="text extra-small">{{ 'translations.diagnostics.feedbackStats.collectMoreAnswers' | transloco }}</div>
            </div>
          </div>
        </ng-template>
      </mat-card>

      <mat-card
        [matTooltip]="getTooltip(stats)"
        class="feedback-stats-item item-consensus"
        *ngIf="stats.type === 'CONSENSUS' && stats.enabled"
      >
        <div class="item-title medium bold">
          <svg class="svg" aria-hidden="true"><use ogSvgUnify="assets/sprite/sprite.svg#join"></use></svg>
          {{ 'translations.diagnostics.feedbackStats.consensus' | transloco }}
        </div>
        <ng-container
          *ngIf="!isInvalidValue(stats.providersConsensus) || !isInvalidValue(stats.userVsProvidersConsensus); else consensusEmpty"
        >
          <div class="consensus-row" *ngIf="!isInvalidValue(stats.providersConsensus)">
            <div class="score medium">{{ formatPercent(stats.providersConsensus, '1.1-2') }}</div>
            <div class="text small">{{ 'translations.diagnostics.feedbackStats.consensusBetweenEvaluators' | transloco }}</div>
          </div>
          <div class="consensus-row" *ngIf="!isInvalidValue(stats.userVsProvidersConsensus)">
            <div class="score medium">{{ formatPercent(stats.userVsProvidersConsensus, '1.1-2') }}</div>
            <div class="text small">
              {{ 'translations.diagnostics.feedbackStats.consensusBetweenUserAndEvaluatorsProviders' | transloco }}
            </div>
          </div>
        </ng-container>
        <ng-template #consensusEmpty>
          <div class="empty-state">
            <mat-icon class="icon">info</mat-icon>
            <div>
              <div class="title small bold">{{ 'translations.diagnostics.feedbackStats.notEnoughData' | transloco }}</div>
              <div class="text extra-small">{{ 'translations.diagnostics.feedbackStats.collectMoreAnswers' | transloco }}</div>
            </div>
          </div>
        </ng-template>
      </mat-card>

      <mat-card
        [matTooltip]="getTooltip(stats)"
        class="feedback-stats-item item-benchmark-college"
        *ngIf="stats.type === 'WORKSPACE_BENCHMARK' && stats.enabled"
      >
        <div class="item-title medium bold">
          {{ 'translations.diagnostics.feedbackStats.workspaceBenchmark' | transloco: { orgName: (organizationData$ | async)?.name } }}
        </div>
        <div class="benchmark-row">
          <div class="score-box">
            <svg class="svg" aria-hidden="true"><use ogSvgUnify="assets/sprite/sprite.svg#bell-curve-2"></use></svg>
            @if (!isInvalidValue(stats.userPercentile)) {
              <div class="score green medium">
                {{
                  'translations.diagnostics.feedbackStats.workspaceBenchmarkScore'
                    | transloco: { score: formatNumber(stats.userPercentile, '1.0-0') }
                }}
              </div>
            }
          </div>
          <div class="text small">
            @if (isInvalidValue(stats.userPercentile)) {
              {{ 'translations.diagnostics.feedbackStats.workspaceBenchmarkInfo' | transloco: { count: MIN_USERS_FOR_BENCHMARK } }}
            } @else {
              {{
                'translations.diagnostics.feedbackStats.workspaceBenchmarkDescription'
                  | transloco
                    : { score: formatNumber(stats.userPercentile, '1.0-0'), totalUsers: formatNumber(stats.totalSelfAssessments, '1.0-0') }
              }}
            }
          </div>
        </div>
      </mat-card>
    </ng-container>
  </div>
</section>
